/**
 * Thin promise wrapper over IndexedDB.
 *
 * Everything lives on this device. There is no server, no sync and no network
 * call anywhere in this app.
 *
 * Stores:
 *   settings  one record, keyed 'settings'
 *   days      one record per date, keyed by date key (YYYY-MM-DD)
 *   meta      small key/value odds and ends, e.g. when the last backup was taken
 */

const DB_NAME = 'timesheet';
const DB_VERSION = 1;

export const STORE_SETTINGS = 'settings';
export const STORE_DAYS = 'days';
export const STORE_META = 'meta';

let dbPromise = null;

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_SETTINGS)) {
        db.createObjectStore(STORE_SETTINGS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_DAYS)) {
        // The key path is the date, so days are naturally sorted and a range
        // query over a week or month is cheap.
        db.createObjectStore(STORE_DAYS, { keyPath: 'date' });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'key' });
      }
    };

    request.onsuccess = () => {
      const db = request.result;
      // If another tab upgrades the schema, close here so it is not blocked.
      db.onversionchange = () => db.close();
      resolve(db);
    };
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('The database is blocked by another open copy of this app.'));
  });
  return dbPromise;
}

/**
 * Run `fn` against a read/write transaction and resolve once it commits.
 *
 * `fn` issues its requests synchronously; we deliberately wait for the
 * transaction rather than the individual requests, so a resolved promise means
 * the data really is durable.
 */
async function withStore(storeNames, mode, fn) {
  const db = await openDb();
  const names = Array.isArray(storeNames) ? storeNames : [storeNames];
  return new Promise((resolve, reject) => {
    const tx = db.transaction(names, mode);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error || new Error('Transaction aborted'));
    try {
      fn(...names.map((n) => tx.objectStore(n)));
    } catch (err) {
      tx.abort();
      reject(err);
    }
  });
}

/** Promise form of a single IDBRequest. */
function promisify(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function get(storeName, key) {
  const db = await openDb();
  const tx = db.transaction(storeName, 'readonly');
  return promisify(tx.objectStore(storeName).get(key));
}

export async function put(storeName, value) {
  return withStore(storeName, 'readwrite', (store) => store.put(value));
}

export async function del(storeName, key) {
  return withStore(storeName, 'readwrite', (store) => store.delete(key));
}

export async function getAll(storeName, query = null) {
  const db = await openDb();
  const tx = db.transaction(storeName, 'readonly');
  return promisify(tx.objectStore(storeName).getAll(query));
}

/** Every record whose key falls between `from` and `to`, inclusive. */
export async function getRange(storeName, from, to) {
  return getAll(storeName, IDBKeyRange.bound(from, to));
}

export async function clear(storeName) {
  return withStore(storeName, 'readwrite', (store) => store.clear());
}

/** Replace the entire contents of a store in one transaction. */
export async function replaceAll(storeName, records) {
  return withStore(storeName, 'readwrite', (store) => {
    store.clear();
    for (const record of records) store.put(record);
  });
}

/** Write many records in one transaction, leaving anything else in place. */
export async function putMany(storeName, records) {
  return withStore(storeName, 'readwrite', (store) => {
    for (const record of records) store.put(record);
  });
}

export async function count(storeName) {
  const db = await openDb();
  const tx = db.transaction(storeName, 'readonly');
  return promisify(tx.objectStore(storeName).count());
}

/** Delete the whole database. Used by "Clear all data". */
export async function deleteDatabase() {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
    dbPromise = null;
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    request.onblocked = () => resolve(); // Another tab holds it open; the delete lands when it closes.
  });
}
